import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearGuestRegistration,
  readGuestRegistration,
  saveGuestRegistration,
} from '../guest-registration-storage';
import {
  captureRegistrationFragment,
  peekPendingRegistrationToken,
  takePendingRegistrationToken,
} from '../registration-fragment';
import type { EventLocationType } from '@/lib/types/events.types';
import { useGuestRegistrationStatus } from './useGuestRegistrationStatus';

/**
 * Who is looking: `resolving` while the session is still being read (neither
 * guest nor member yet), `guest` with no session, `member` with one.
 */
export type ViewerStatus = 'resolving' | 'guest' | 'member';

export interface NonMemberRegistration {
  /** True only once the API has confirmed the remembered registration is live (FR-290); never from the browser's say-so alone. */
  isRegistered: boolean;
  /** True while a remembered token is being checked: the page shows neither Register nor the registered state yet. */
  isChecking: boolean;
  /** The remembered token, for the cancel request; null when none. Never rendered or logged. */
  token: string | null;
  /** Join details, only once the API has confirmed the registration; null otherwise (and for an ended event). */
  locationType: EventLocationType | null;
  locationDetails: string | null;
  /** Re-read the remembered state; call after a registration succeeded. */
  refresh: () => void;
}

/**
 * The remembered non-member registration for one event (FR-285, FR-286): what
 * the browser holds, what the email link brings, and what the API says about it.
 *
 * - `member` or `resolving`: ignores the stored state entirely and asks
 *   nothing; the account's own registration status decides. Waiting rather
 *   than assuming logged out, as `ViewerAdaptiveLayout` does.
 * - A `#r=<token>` fragment is taken off the address bar for every viewer, on
 *   mount and on each later navigation (an in-place hash change, a pasted
 *   link). Only a `guest` stores it for this event; a member's is discarded and
 *   one that arrives while the session resolves waits until it settles. The
 *   token may already be in memory: `src/instrument.ts` takes it before React
 *   starts, so Sentry never records it.
 * - The registered state shows ONLY when the status read confirms it (FR-290,
 *   NM-4 AC8): a stored or waiting token is checked first (`isChecking`, so
 *   Register does not flash up for a registered visitor either), and a
 *   `{ isRegistered: false }` answer clears the entry and Register returns,
 *   so a cancelled registration is never shown as registered. A failed read
 *   confirms nothing: the entry is kept and Register shows, which is safe to
 *   resubmit (FR-284).
 */
export function useNonMemberRegistration(eventId: string, viewer: ViewerStatus): NonMemberRegistration {
  const isGuest = viewer === 'guest';
  const { key: locationKey } = useLocation();
  // Only a re-render trigger: the remembered state lives in storage and memory, not React state.
  const [, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  // Read while rendering (reads never write), so the first paint is already right.
  const stored = isGuest ? readGuestRegistration(eventId) : null;
  const waitingToken = isGuest ? peekPendingRegistrationToken(eventId) : null;
  // The email link carries the token only, so keep an email already known for this event.
  const entry = waitingToken ? { email: stored?.email ?? '', token: waitingToken } : stored;

  useEffect(() => {
    captureRegistrationFragment();
    if (!eventId || viewer === 'resolving') return;
    const token = takePendingRegistrationToken(eventId);
    if (token === null || viewer !== 'guest') return;
    saveGuestRegistration(eventId, { email: readGuestRegistration(eventId)?.email ?? '', token });
    refresh();
  }, [eventId, viewer, locationKey, refresh]);

  const { data: status, isLoading: isStatusLoading } = useGuestRegistrationStatus(eventId, entry?.token, isGuest);
  const isStale = status?.isRegistered === false;

  useEffect(() => {
    if (!isStale) return;
    clearGuestRegistration(eventId);
    refresh();
  }, [isStale, eventId, refresh]);

  const isRegistered = entry !== null && status?.isRegistered === true;
  return {
    isRegistered,
    isChecking: entry !== null && isStatusLoading,
    token: entry?.token ?? null,
    locationType: isRegistered ? status.locationType : null,
    locationDetails: isRegistered ? status.locationDetails : null,
    refresh,
  };
}
